import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { columnApi } from '../services/columnApi';
import { BoardColumnCreate, BoardColumnUpdate, UserBoardColumnPreferencesUpdate } from '../types';
import { TASKS_QUERY_KEY, MY_TASKS_QUERY_KEY, CROSS_BOARD_TASKS_QUERY_KEY } from './useTasks';
import { GROUPS_QUERY_KEY } from './useGroups';
import { BOARD_SUMMARIES_QUERY_KEY } from './useBoardSummaries';

export const COLUMNS_QUERY_KEY = 'board-columns';
export const COLUMN_VALUES_QUERY_KEY = 'item-column-values';
export const MOVE_PREVIEW_QUERY_KEY = 'item-move-preview';
export const COLUMN_PREFERENCES_QUERY_KEY = 'board-column-preferences';

export function useColumns(boardId: string, options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  return useQuery({
    queryKey: [COLUMNS_QUERY_KEY, boardId],
    queryFn: () => columnApi.list(boardId),
    enabled: enabled && !!boardId,
    staleTime: 10_000,
  });
}

export function useColumn(columnId: string) {
  return useQuery({
    queryKey: [COLUMNS_QUERY_KEY, 'single', columnId],
    queryFn: () => columnApi.get(columnId),
    enabled: !!columnId,
  });
}

export function useCreateColumn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ boardId, data }: { boardId: string; data: BoardColumnCreate }) =>
      columnApi.create(boardId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [COLUMNS_QUERY_KEY, variables.boardId] });
    },
  });
}

export function useUpdateColumn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ columnId, data }: { columnId: string; data: BoardColumnUpdate }) =>
      columnApi.update(columnId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COLUMNS_QUERY_KEY] });
    },
  });
}

export function useDeleteColumn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (columnId: string) => columnApi.delete(columnId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COLUMNS_QUERY_KEY] });
    },
  });
}

export function useReorderColumn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ columnId, position }: { columnId: string; position: number }) =>
      columnApi.reorder(columnId, position),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COLUMNS_QUERY_KEY] });
    },
  });
}

// Item Column Values
export function useTaskColumnValues(taskId: string) {
  return useQuery({
    queryKey: [COLUMN_VALUES_QUERY_KEY, taskId],
    queryFn: () => columnApi.getItemValues(taskId),
    enabled: !!taskId,
  });
}

export function useSetTaskColumnValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      columnKey,
      value,
    }: {
      taskId: string;
      columnKey: string;
      value: unknown;
    }) => columnApi.setItemValue(taskId, columnKey, value),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [COLUMN_VALUES_QUERY_KEY, variables.taskId] });
      queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MY_TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CROSS_BOARD_TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [BOARD_SUMMARIES_QUERY_KEY] });
    },
  });
}

// Move Preview
export function useMovePreview(taskId: string, targetBoardId: string | null) {
  return useQuery({
    queryKey: [MOVE_PREVIEW_QUERY_KEY, taskId, targetBoardId],
    queryFn: () => columnApi.getMovePreview(taskId, targetBoardId!),
    enabled: !!taskId && !!targetBoardId,
  });
}

export function useMoveToBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      targetBoardId,
      targetGroupId,
    }: {
      taskId: string;
      targetBoardId: string;
      targetGroupId?: string;
    }) => columnApi.moveToBoard(taskId, targetBoardId, targetGroupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['boards'] });
      queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [BOARD_SUMMARIES_QUERY_KEY] });
      // Invalidate sprint items queries so sprint kanban view reflects the board change
      queryClient.invalidateQueries({ queryKey: ['sprints', 'detail'] });
    },
  });
}

// User Board Column Preferences
export function useColumnPreferences(boardId: string) {
  return useQuery({
    queryKey: [COLUMN_PREFERENCES_QUERY_KEY, boardId],
    queryFn: () => columnApi.getColumnPreferences(boardId),
    enabled: !!boardId,
    staleTime: 10_000,
  });
}

export function useUpdateColumnPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      boardId,
      data,
    }: {
      boardId: string;
      data: UserBoardColumnPreferencesUpdate;
    }) => columnApi.updateColumnPreferences(boardId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [COLUMN_PREFERENCES_QUERY_KEY, variables.boardId],
      });
    },
  });
}
